import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  DollarSign, TrendingUp, TrendingDown, RefreshCw,
} from 'lucide-react';

/**
 * CostAnalyticsPanel — rolling P&L per user (iter105.8).
 *
 * GET /api/admin/cost-analytics?days=30&sort=margin_usd&order=asc returns:
 *   { totals: {revenue_usd, emergent_cost_usd, stripe_fees_usd, margin_usd, margin_pct, ...},
 *     users:  [{email, plan, credits_used, emergent_cost_usd, revenue_usd, margin_usd, margin_pct, top_source}, ...] }
 */

const RANGE_OPTIONS = [
  { label: '7d', value: 7 },
  { label: '30d', value: 30 },
  { label: '90d', value: 90 },
];

const money = (n) => (typeof n === 'number' ? `$${n.toFixed(2)}` : '—');
const pct = (n) => (typeof n === 'number' ? `${n.toFixed(1)}%` : '—');

const marginBadge = (pctVal) => {
  if (pctVal === null || pctVal === undefined) {
    return <Badge className="bg-slate-700/40 border-slate-600 text-slate-300 text-[10px]">n/a</Badge>;
  }
  if (pctVal >= 60) {
    return <Badge className="bg-emerald-500/20 border-emerald-500/40 text-emerald-200 text-[10px]">{pct(pctVal)}</Badge>;
  }
  if (pctVal >= 30) {
    return <Badge className="bg-amber-500/20 border-amber-500/40 text-amber-200 text-[10px]">{pct(pctVal)}</Badge>;
  }
  if (pctVal >= 0) {
    return <Badge className="bg-orange-500/20 border-orange-500/40 text-orange-200 text-[10px]">{pct(pctVal)}</Badge>;
  }
  return <Badge className="bg-rose-500/20 border-rose-500/40 text-rose-200 text-[10px]">{pct(pctVal)}</Badge>;
};

const CostAnalyticsPanel = () => {
  const [days, setDays] = useState(30);
  const [sort, setSort] = useState('margin_usd');
  const [order, setOrder] = useState('asc');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    try {
      setRefreshing(true);
      const res = await api.get('/admin/cost-analytics', {
        params: { days, sort, order, top: 25 },
      });
      setData(res.data);
    } catch (e) {
      setData({ error: e.response?.data?.detail || 'Failed to load cost analytics' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [days, sort, order]);

  useEffect(() => {
    load();
  }, [load]);

  const t = data?.totals;
  const marginTone = useMemo(() => {
    if (!t || t.margin_usd === undefined) return 'text-slate-300';
    if (t.margin_usd < 0) return 'text-rose-300';
    if (t.margin_pct !== null && t.margin_pct >= 60) return 'text-emerald-300';
    return 'text-amber-300';
  }, [t]);

  if (loading) {
    return (
      <Card data-testid="cost-analytics-panel">
        <CardHeader className="pb-3">
          <Skeleton className="h-5 w-56" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      data-testid="cost-analytics-panel"
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-emerald-400" />
            <CardTitle className="text-base">Cost Analytics — Live P&amp;L</CardTitle>
          </div>
          <div className="flex items-center gap-1">
            {RANGE_OPTIONS.map((opt) => (
              <Button
                key={opt.value}
                type="button"
                size="sm"
                variant={days === opt.value ? 'default' : 'outline'}
                onClick={() => setDays(opt.value)}
                className={
                  days === opt.value
                    ? 'bg-[#DC2626] hover:bg-[#B91C1C] text-white h-7 px-2 text-xs'
                    : 'border-[#1F2A4D] bg-[#0A1024] text-slate-300 h-7 px-2 text-xs'
                }
                data-testid={`cost-range-${opt.value}`}
              >
                {opt.label}
              </Button>
            ))}
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={load}
              disabled={refreshing}
              className="ml-1"
              data-testid="cost-analytics-refresh"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
        <CardDescription>
          Emergent LLM $ spend vs subscription + wallet revenue, per user. Rolling window.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {data?.error && (
          <p className="text-sm text-rose-300" data-testid="cost-analytics-error">
            {data.error}
          </p>
        )}

        {t && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="cost-analytics-totals">
            <div className="border border-[#1F2A4D] rounded-md p-3">
              <div className="text-[10px] uppercase tracking-wide text-slate-500">
                Revenue
              </div>
              <div className="text-lg font-semibold text-emerald-300" data-testid="cost-total-revenue">
                {money(t.revenue_usd)}
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">
                {t.users_with_activity} users · {t.credits_used.toLocaleString()} credits
              </div>
            </div>
            <div className="border border-[#1F2A4D] rounded-md p-3">
              <div className="text-[10px] uppercase tracking-wide text-slate-500">
                Emergent LLM spend
              </div>
              <div className="text-lg font-semibold text-rose-300" data-testid="cost-total-emergent">
                {money(t.emergent_cost_usd)}
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">
                pass-through via Universal Key
              </div>
            </div>
            <div className="border border-[#1F2A4D] rounded-md p-3">
              <div className="text-[10px] uppercase tracking-wide text-slate-500">
                Stripe fees
              </div>
              <div className="text-lg font-semibold text-slate-300" data-testid="cost-total-stripe">
                {money(t.stripe_fees_usd)}
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">
                2.9% + $0.30 per charge
              </div>
            </div>
            <div className="border border-[#1F2A4D] rounded-md p-3">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 flex items-center gap-1">
                {t.margin_usd >= 0 ? (
                  <TrendingUp className="h-3 w-3 text-emerald-400" />
                ) : (
                  <TrendingDown className="h-3 w-3 text-rose-400" />
                )}
                Net margin
              </div>
              <div className={`text-lg font-semibold ${marginTone}`} data-testid="cost-total-margin">
                {money(t.margin_usd)}
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">
                {t.margin_pct !== null ? pct(t.margin_pct) : '—'} of revenue
              </div>
            </div>
          </div>
        )}

        {data?.users && (
          <div className="border border-[#1F2A4D] rounded-md overflow-x-auto" data-testid="cost-analytics-table-wrap">
            <Table>
              <TableHeader>
                <TableRow className="border-[#1F2A4D] hover:bg-transparent">
                  <TableHead className="text-xs">User</TableHead>
                  <TableHead className="text-xs">Plan</TableHead>
                  <TableHead className="text-xs text-right">Credits</TableHead>
                  <TableHead className="text-xs text-right">Revenue</TableHead>
                  <TableHead className="text-xs text-right">Emergent $</TableHead>
                  <TableHead className="text-xs text-right">Margin $</TableHead>
                  <TableHead className="text-xs text-right">Margin %</TableHead>
                  <TableHead className="text-xs">Top source</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-slate-500 text-xs py-6">
                      No activity in the last {days} days.
                    </TableCell>
                  </TableRow>
                ) : (
                  data.users.map((u) => (
                    <TableRow
                      key={u.user_id}
                      className="border-[#1F2A4D] hover:bg-[#131C3A]/40"
                      data-testid={`cost-row-${u.user_id}`}
                    >
                      <TableCell className="text-xs text-slate-200 truncate max-w-[220px]">
                        {u.email || u.user_id}
                        {u.is_admin && (
                          <Badge className="ml-1 bg-slate-700/60 text-slate-300 text-[9px] border-slate-600">
                            admin
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-slate-400">{u.plan || 'unknown'}</TableCell>
                      <TableCell className="text-xs text-right text-slate-300">
                        {u.credits_used.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-xs text-right text-emerald-300">
                        {money(u.revenue_usd)}
                      </TableCell>
                      <TableCell className="text-xs text-right text-rose-300">
                        {money(u.emergent_cost_usd)}
                      </TableCell>
                      <TableCell
                        className={`text-xs text-right font-semibold ${
                          u.margin_usd >= 0 ? 'text-emerald-200' : 'text-rose-300'
                        }`}
                      >
                        {money(u.margin_usd)}
                      </TableCell>
                      <TableCell className="text-xs text-right">
                        {marginBadge(u.margin_pct)}
                      </TableCell>
                      <TableCell className="text-[10px] text-slate-500 truncate max-w-[140px]">
                        {u.top_source || '—'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}

        <p className="text-[10px] text-slate-500">
          Revenue is pro-rated ({days}/30 × plan monthly price) + wallet purchases in the window. Emergent LLM cost is estimated
          from per-source token counts (see `routes/admin_cost_analytics.py`). Refresh anytime.
        </p>
      </CardContent>
    </Card>
  );
};

export default CostAnalyticsPanel;
